import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  Signal,
  signal,
  viewChild,
} from '@angular/core';
import {
  disabled,
  form,
  FormField,
  min,
  required,
} from '@angular/forms/signals';
import { Bundle } from '@core/core.types';
import { ProductsListState } from '@core/store/products/products.state';
import { momentToNgbDate } from '@core/utils/core.util';
import {
  NgbDatepickerModule,
  NgbTooltipModule,
  NgbTypeahead,
  NgbTypeaheadModule,
} from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { LoadingService } from '@shared/components/loading-dialog/loading-dialog.component';
import { SelectOptionComponent } from '@shared/components/select/select-option/select-option.component';
import { SelectComponent } from '@shared/components/select/select.component';
import moment from 'moment';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  Subject,
  tap,
} from 'rxjs';
import { AddPricingCountryBundlePriceAction } from '../../../store/pricing-country.actions';
import { PricingCountryState } from '../../../store/pricing-country.state';
import { CountryPriceType, PricingCountry } from '../../../types/pricing.types';

interface CountryBundlePriceModel {
  bundle: Bundle | null;
  type: CountryPriceType;
  price: number | null;
  validFrom: moment.Moment | null;
}

const EMPTY_MODEL: CountryBundlePriceModel = {
  bundle: null,
  type: CountryPriceType.EUP,
  price: null,
  validFrom: null,
};

@Component({
  selector: 'app-country-bundle-price-form',
  imports: [
    FormField,
    NgbTypeaheadModule,
    NgbDatepickerModule,
    NgbTooltipModule,
    TranslatePipe,
    SelectComponent,
    SelectOptionComponent,
  ],
  templateUrl: './country-bundle-price-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './country-bundle-price-form.component.scss',
})
export class CountryBundlePriceFormComponent {
  public countryId = input.required<string>();

  private store = inject(Store);
  private loadingService = inject(LoadingService);
  private translateService = inject(TranslateService);

  protected focus$ = new Subject<string>();
  protected click$ = new Subject<string>();

  protected pricingCountry: Signal<PricingCountry> = computed(() =>
    this.store.selectSignal(PricingCountryState.getPricingCountryById)()(
      this.countryId()
    )
  );
  protected bundlesList: Signal<Bundle[]> = computed(() =>
    this.store.selectSignal(ProductsListState.getBundlesList)()
  );
  /**
   * Country currency is not part of the backend model yet, so unlike the
   * partner form, a missing currency only shows a hint and does not block
   * price entry.
   */
  protected noCurrencyWarning = computed(
    () => !!this.pricingCountry() && !this.pricingCountry().currency
  );

  protected priceModel = signal<CountryBundlePriceModel>({ ...EMPTY_MODEL });

  /**
   * True when the selected bundle has no price of the selected type yet; the
   * date is then locked and `null` is sent (backend applies 1 January 1970).
   */
  protected firstTimePrice = computed(() => {
    const { bundle, type } = this.priceModel();

    return !!bundle && !this.hasPrice(bundle, type);
  });

  protected priceForm = form(this.priceModel, (path) => {
    required(path.bundle);
    required(path.type);
    required(path.price);
    min(path.price, 1);
    disabled(
      path.validFrom,
      () => !this.priceModel().bundle || this.firstTimePrice()
    );
  });

  protected canSubmit = computed(
    () =>
      this.priceForm().valid() &&
      (this.firstTimePrice() || !!this.priceModel().validFrom)
  );

  protected minDate = moment().add(1, 'day');
  protected maxDate = moment().add(100, 'year');
  protected startDate = moment().add(1, 'month');

  protected readonly priceTypes = Object.values(CountryPriceType);
  protected readonly momentToNgbDate = momentToNgbDate;

  private typeaheadInstance = viewChild<NgbTypeahead>('instance');
  private bundleTypeaheadElement = viewChild<ElementRef<HTMLInputElement>>(
    'bundleTypeaheadElement'
  );
  private lastSearchTerm = '';

  protected search = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(
      tap((term) => {
        if (this.lastSearchTerm !== term) {
          this.setBundle(null);
        }

        this.lastSearchTerm = term;
      }),
      debounceTime(200),
      distinctUntilChanged()
    );
    const clicksWithClosedPopup$ = this.click$.pipe(
      filter(() => !this.typeaheadInstance()?.isPopupOpen())
    );

    return merge(debouncedText$, this.focus$, clicksWithClosedPopup$).pipe(
      map((term) =>
        this.bundlesList()?.filter(
          (bundle) =>
            bundle.internalName.toLowerCase().indexOf(term.toLowerCase()) > -1
        )
      )
    );
  };

  protected inputFormatter = (item: Bundle) => item?.name ?? '';

  protected setBundle(bundle: Bundle | null) {
    this.priceModel.update((model) => ({
      ...model,
      bundle,
      validFrom: this.defaultValidFrom(bundle, model.type),
    }));
  }

  protected setType(type: CountryPriceType) {
    this.priceModel.update((model) => ({
      ...model,
      type,
      validFrom: this.defaultValidFrom(model.bundle, type),
    }));
  }

  private defaultValidFrom(
    bundle: Bundle | null,
    type: CountryPriceType
  ): moment.Moment | null {
    return bundle && !this.hasPrice(bundle, type)
      ? moment().year(1970).month(0).date(1)
      : null;
  }

  private hasPrice(bundle: Bundle, type: CountryPriceType): boolean {
    return !!this.pricingCountry()?.pricelist?.some(
      (bundlePrice) =>
        bundlePrice.id === bundle.id &&
        bundlePrice.pivot.type === type &&
        !!bundlePrice.pivot.price
    );
  }

  protected addPrice() {
    if (!this.canSubmit()) {
      return;
    }

    const { bundle, type, price, validFrom } = this.priceModel();

    this.loadingService.wait({
      message: this.translateService.instant(
        'pricing.country.details.loading.addingPrice'
      ),
      observableToFinish$: this.store.dispatch(
        new AddPricingCountryBundlePriceAction(
          this.countryId(),
          bundle.id,
          price,
          type,
          this.firstTimePrice() ? null : (validFrom?.toDate() ?? null)
        )
      ),
    });

    this.priceModel.set({ ...EMPTY_MODEL, type });
    this.priceForm().reset();
    this.lastSearchTerm = '';
    this.bundleTypeaheadElement().nativeElement.value = '';
  }
}
